import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { CircleCheck } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import {
  getCardContainerClass,
  getCardHeadingSize,
  getCardPaddingClass,
  getContentGapClass,
  getHeaderSpacingClass,
  getSubtitleClass,
  getSubtitleTextStyle,
} from './productSupportSuccessLayout';

import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import {
  PRODUCT_SUPPORT_FOLLOW_UP_DESCRIPTION,
  PRODUCT_SUPPORT_GUIDELINES,
  PRODUCT_SUPPORT_SCOPE_DESCRIPTION,
  TREATMENT_SUPPORT_LABEL,
} from '@/constants/medicalOnDemand';
import { MOTION } from '@/constants/motion';
import { useEntranceAnimation } from '@/hooks/useEntranceAnimation';
import { usePressAnimation } from '@/hooks/usePressAnimation';
import { useStaggeredEntrance } from '@/hooks/useStaggeredEntrance';

function productSupportHeadingSize(
  mobileWeb: boolean,
  nativeCompact: boolean
): 'h1-xl' | 'h2-lg' | 'h1-2xl' {
  if (mobileWeb) {
    return 'h1-xl';
  }

  if (nativeCompact) {
    return 'h2-lg';
  }

  return 'h1-2xl';
}

type ProductSupportSuccessProps = {
  productName: string;
  onContinueToProductSupport: () => void;
  onGoToInbox: () => void;
  onPrimaryCtaPress: () => void;
  onViewed: () => void;
};

/** Product Support success card shown when inquiry is eligible for MOD follow-up. */
export function ProductSupportSuccess({
  productName,
  onContinueToProductSupport,
  onGoToInbox,
  onPrimaryCtaPress,
  onViewed,
}: ProductSupportSuccessProps) {
  const badgeStyle = useEntranceAnimation({ ...MOTION.entrance.emphasis, delay: 0 });

  const headingStyle = useEntranceAnimation({ ...MOTION.entrance.subtle, delay: 100 });

  const subtitleStyle = useEntranceAnimation({ ...MOTION.entrance.subtle, delay: 180 });

  const cardStyle = useEntranceAnimation({ ...MOTION.entrance.standard, delay: 250 });

  const guidelineStyles = useStaggeredEntrance({
    count: PRODUCT_SUPPORT_GUIDELINES.sections.length,
    ...MOTION.entrance.sequential,
    ...MOTION.stagger.standard,
    baseDelay: 320,
  });

  const ctaStyle = useEntranceAnimation({ ...MOTION.entrance.subtle, delay: 500 });

  const primaryPress = usePressAnimation(MOTION.press.standard);

  const linkPress = usePressAnimation({ pressedScale: 0.95, spring: MOTION.press.standard.spring });

  const checkIconOpacity = useSharedValue(0);

  const checkIconScale = useSharedValue(0.35);

  const checkIconRotate = useSharedValue(-28);

  const checkIconTranslateY = useSharedValue(-6);

  const { bottom } = useSafeAreaInsets();

  const isCompactLayout = useBreakpointValue({ default: true, md: false }) ?? !isWeb;

  const isNativeCompact = !isWeb && isCompactLayout;

  const [isMobileWeb, setIsMobileWeb] = useState(false);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const check = () => setIsMobileWeb(window.innerWidth < 640);

    check();

    window.addEventListener('resize', check);

    return () => window.removeEventListener('resize', check);
  }, []);

  const showStickyButtons = isNativeCompact || isMobileWeb;

  const shouldAnimateCheckIcon = true;

  const layoutParams = {
    isCompactLayout: isNativeCompact || isMobileWeb,
    isMobileWeb,
    isNativeCompact,
  };

  const headerSpacingClass = getHeaderSpacingClass(layoutParams);

  const headingSize = productSupportHeadingSize(isMobileWeb, isNativeCompact);

  const headingClass = isNativeCompact
    ? 'mb-0 mt-1 max-w-[22rem] leading-tight tracking-tight'
    : 'mt-4 max-w-2xl';

  const subtitleClass = getSubtitleClass(layoutParams);

  const subtitleTextStyle = getSubtitleTextStyle(layoutParams);

  const cardContainerClass = getCardContainerClass(layoutParams);

  const cardPaddingClass = getCardPaddingClass(layoutParams);

  const contentGapClass = getContentGapClass(layoutParams);

  const cardHeadingSize = getCardHeadingSize(layoutParams);

  useEffect(() => {
    onViewed();
  }, [onViewed]);

  useEffect(() => {
    if (!shouldAnimateCheckIcon) {
      checkIconOpacity.value = 1;

      checkIconScale.value = 1;

      checkIconRotate.value = 0;

      checkIconTranslateY.value = 0;

      return;
    }

    checkIconOpacity.value = withDelay(120, withTiming(1, { duration: 180 }));

    checkIconScale.value = withDelay(
      120,
      withSequence(
        withTiming(1.15, {
          duration: 220,
          easing: Easing.out(Easing.back(1.2)),
        }),
        withSpring(1, {
          damping: 11,
          stiffness: 240,
          mass: 0.4,
        })
      )
    );

    checkIconRotate.value = withDelay(
      120,
      withTiming(0, {
        duration: 260,
        easing: Easing.out(Easing.cubic),
      })
    );

    checkIconTranslateY.value = withDelay(
      120,
      withTiming(0, {
        duration: 220,
        easing: Easing.out(Easing.cubic),
      })
    );
  }, [
    checkIconOpacity,
    checkIconRotate,
    checkIconScale,
    checkIconTranslateY,
    shouldAnimateCheckIcon,
  ]);

  const checkIconAnimatedStyle = useAnimatedStyle(() => ({
    opacity: checkIconOpacity.value,
    transform: [
      { translateY: checkIconTranslateY.value },
      { scale: checkIconScale.value },
      { rotate: `${checkIconRotate.value}deg` },
    ],
  }));

  return (
    <VStack
      className={twJoin(
        'mx-auto w-full max-w-3xl flex-1 md:px-12',
        isNativeCompact ? 'px-4' : 'px-6'
      )}>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingBottom: bottom + (isNativeCompact ? 140 : 24),
          ...(isWeb ? { flexGrow: 1 } : {}),
        }}
        showsVerticalScrollIndicator={false}>
        <VStack className={twJoin('items-center px-2', headerSpacingClass)}>
          {!isNativeCompact && (
            <Animated.View style={badgeStyle}>
              <Badge
                action="success"
                variant="outline"
                size="lg"
                className="rounded-full border-success-300 bg-success-50">
                {shouldAnimateCheckIcon ? (
                  <Animated.View style={checkIconAnimatedStyle}>
                    <Icon as={CircleCheck} size="xl" className="mr-1 text-success-600" />
                  </Animated.View>
                ) : (
                  <Icon as={CircleCheck} size="xl" className="mr-1 text-success-600" />
                )}
                <BadgeText className="text-success-700">Inquiry Submitted</BadgeText>
              </Badge>
            </Animated.View>
          )}

          <Animated.View style={headingStyle}>
            <Heading
              size={headingSize}
              className={twJoin('text-center text-typography-0', headingClass)}>
              Your question may qualify for specialist follow-up.
            </Heading>
          </Animated.View>
          <Animated.View style={subtitleStyle}>
            <Text
              className={twJoin('text-center text-typography-0', subtitleClass)}
              style={subtitleTextStyle}>
              {PRODUCT_SUPPORT_FOLLOW_UP_DESCRIPTION}
            </Text>
          </Animated.View>
        </VStack>

        <Animated.View style={cardStyle} className={cardContainerClass}>
          <Card
            className={twJoin(
              'rounded-xl border border-primary-300 bg-background-0 md:p-8',
              cardPaddingClass
            )}>
            <VStack className={twJoin(contentGapClass)}>
              <Heading size={cardHeadingSize}>Edit & Send to {TREATMENT_SUPPORT_LABEL}</Heading>

              {isNativeCompact ? (
                <>
                  <Text className="text-sm text-typography-700">
                    {PRODUCT_SUPPORT_SCOPE_DESCRIPTION}
                  </Text>
                  {PRODUCT_SUPPORT_GUIDELINES.sections.map((section, sectionIndex) => {
                    const isCanAsk = section.heading.toLowerCase().includes('can ask');

                    const itemsToShow = section.items;

                    return (
                      <Animated.View key={sectionIndex} style={guidelineStyles[sectionIndex]}>
                        <VStack className="gap-1">
                          <Text
                            className={twJoin(
                              'font-semibold text-sm',
                              isCanAsk ? 'text-success-600' : 'text-warning-600'
                            )}>
                            {section.heading}
                          </Text>
                          {itemsToShow.map((item, itemIndex) => (
                            <HStack key={itemIndex} className="items-start gap-1">
                              <Text
                                className={twJoin(
                                  'font-semibold text-sm',
                                  isCanAsk ? 'text-success-600' : 'text-warning-600'
                                )}>
                                {isCanAsk ? '✓' : '✕'}
                              </Text>
                              <Text className="flex-1 text-sm leading-5 text-typography-800">
                                {item}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Animated.View>
                    );
                  })}
                </>
              ) : (
                <VStack className="gap-3">
                  <Text className="text-base text-typography-700">
                    {PRODUCT_SUPPORT_SCOPE_DESCRIPTION}
                  </Text>
                  <HStack className="flex-col items-stretch gap-3 sm:flex-row">
                    {PRODUCT_SUPPORT_GUIDELINES.sections.map((section, sectionIndex) => {
                      const isCanAsk = section.heading.toLowerCase().includes('can ask');

                      return (
                        <Animated.View
                          key={sectionIndex}
                          className="min-w-0 sm:flex-1"
                          style={guidelineStyles[sectionIndex]}>
                          <Card
                            className={twJoin(
                              'rounded-lg border p-3 shadow-hard-1 web:cursor-default web:transition-all web:duration-150 sm:flex-1',
                              isCanAsk
                                ? 'border-success-200 web:hover:bg-success-0'
                                : 'border-warning-200 web:hover:bg-warning-50'
                            )}>
                            <VStack className="gap-2">
                              <Badge
                                action={isCanAsk ? 'success' : 'warning'}
                                variant="outline"
                                size="md"
                                className="self-start rounded-full px-3">
                                <BadgeText>{section.heading}</BadgeText>
                              </Badge>

                              <VStack className="gap-1.5">
                                {section.items.map((item, itemIndex) => (
                                  <HStack key={itemIndex} className="items-start gap-2">
                                    <Text
                                      className={twJoin(
                                        'pt-0.5 font-semibold text-sm',
                                        isCanAsk ? 'text-success-600' : 'text-warning-600'
                                      )}>
                                      {isCanAsk ? '✓' : '✕'}
                                    </Text>
                                    <Text className="flex-1 text-sm leading-6 text-typography-800">
                                      {item}
                                    </Text>
                                  </HStack>
                                ))}
                              </VStack>
                            </VStack>
                          </Card>
                        </Animated.View>
                      );
                    })}
                  </HStack>
                </VStack>
              )}

              {!showStickyButtons && (
                <Animated.View style={ctaStyle}>
                  <VStack className="items-center gap-3">
                    <Animated.View style={primaryPress.animatedStyle} className="w-auto">
                      <Button
                        action="primary"
                        size="lg"
                        className={twJoin(
                          'w-auto px-12',
                          MOTION.interactive.btnPrimary.base,
                          MOTION.interactive.btnPrimary.hover,
                          MOTION.interactive.btnPrimary.active
                        )}
                        onPressIn={primaryPress.onPressIn}
                        onPressOut={primaryPress.onPressOut}
                        onPress={() => {
                          onPrimaryCtaPress();

                          onContinueToProductSupport();
                        }}>
                        <ButtonText>Edit & Send to {TREATMENT_SUPPORT_LABEL}</ButtonText>
                      </Button>
                    </Animated.View>

                    <Animated.View style={linkPress.animatedStyle}>
                      <Button
                        variant="link"
                        size="lg"
                        className={twJoin('px-8', MOTION.interactive.btnLink.base)}
                        onPressIn={linkPress.onPressIn}
                        onPressOut={linkPress.onPressOut}
                        onPress={onGoToInbox}>
                        <ButtonText
                          className={twJoin(
                            'text-primary-600 data-[hover=true]:text-primary-700',
                            MOTION.interactive.linkText.base,
                            MOTION.interactive.linkText.hover
                          )}>
                          Continue to Inbox
                        </ButtonText>
                      </Button>
                    </Animated.View>
                  </VStack>
                </Animated.View>
              )}
            </VStack>
          </Card>
        </Animated.View>
      </ScrollView>

      {showStickyButtons && (
        <View
          className={twJoin(
            'w-screen rounded-t-2xl border-t-2 border-primary-500 bg-background-0 pt-2',
            isNativeCompact ? '-mx-4 px-4' : '-mx-6 px-6'
          )}>
          <Animated.View style={ctaStyle}>
            <VStack className="gap-0" style={{ paddingBottom: Math.max(bottom, 8) }}>
              <Animated.View style={primaryPress.animatedStyle} className="w-full">
                <Button
                  action="primary"
                  size="lg"
                  className={twJoin(
                    'min-h-[44px] w-full rounded-xl',
                    MOTION.interactive.btnPrimary.base,
                    MOTION.interactive.btnPrimary.hover,
                    MOTION.interactive.btnPrimary.active
                  )}
                  onPressIn={primaryPress.onPressIn}
                  onPressOut={primaryPress.onPressOut}
                  onPress={() => {
                    onPrimaryCtaPress();

                    onContinueToProductSupport();
                  }}>
                  <ButtonText className="font-semibold text-base text-typography-0">
                    Edit & Send to {TREATMENT_SUPPORT_LABEL}
                  </ButtonText>
                </Button>
              </Animated.View>

              <Animated.View style={linkPress.animatedStyle}>
                <Button
                  variant="link"
                  className={twJoin('py-0.5', MOTION.interactive.btnLink.base)}
                  onPressIn={linkPress.onPressIn}
                  onPressOut={linkPress.onPressOut}
                  onPress={onGoToInbox}>
                  <ButtonText className="text-base text-primary-700">Continue to Inbox</ButtonText>
                </Button>
              </Animated.View>
            </VStack>
          </Animated.View>
        </View>
      )}
    </VStack>
  );
}
